// Admin → API keys (brief §5), ported from prototype/src/pages/Admin.jsx KEY_DEFS. Values are
// stored AES-256-GCM encrypted on the server and never sent back to the browser after saving.

export type ApiKeyProviderName =
  | 'GEMINI'
  | 'OPENAI'
  | 'ANTHROPIC'
  | 'GOOGLE_PLACES'
  | 'RAZORPAY_KEY_ID'
  | 'RAZORPAY_KEY_SECRET'
  | 'RAZORPAY_WEBHOOK_SECRET'
  | 'SMTP_HOST'
  | 'SMTP_PORT'
  | 'SMTP_USER'
  | 'SMTP_PASSWORD'
  | 'TURNSTILE_SITE_KEY'
  | 'TURNSTILE_SECRET_KEY';

export interface ApiKeyDefinition {
  provider: ApiKeyProviderName;
  label: string;
  placeholder: string;
  help?: string;
  secret: boolean; // masked input; value never shown again
  testable: boolean; // has its own Test button
  testWith?: ApiKeyProviderName; // tested as part of another key's Test
}

export interface ApiKeyGroup {
  group: string;
  items: ApiKeyDefinition[];
}

export const API_KEY_GROUPS: ApiKeyGroup[] = [
  {
    group: 'AI engines',
    items: [
      {
        provider: 'GEMINI',
        label: 'Google Gemini API key',
        placeholder: 'AIza… or AQ.…',
        help: "Google AI Studio → Get API key. Recommended engine: low cost for chat and reading astrologers' websites.",
        secret: true,
        testable: true,
      },
      {
        provider: 'OPENAI',
        label: 'OpenAI (ChatGPT) API key',
        placeholder: 'sk-proj-…',
        help: 'platform.openai.com → API keys. Needs a funded account.',
        secret: true,
        testable: true,
      },
      {
        provider: 'ANTHROPIC',
        label: 'Anthropic (Claude) API key',
        placeholder: 'sk-ant-…',
        help: 'console.anthropic.com → API keys.',
        secret: true,
        testable: true,
      },
      {
        provider: 'GOOGLE_PLACES',
        label: 'Google Places API key (map panel & review summaries)',
        placeholder: 'AIza… or AQ.…',
        help: 'Google Cloud Console → enable Places API (New). Used for the “On Google Maps” panel and when a client taps “See review summary” — nothing is stored except place IDs.',
        secret: true,
        testable: true,
      },
    ],
  },
  {
    group: 'Payments — Razorpay',
    items: [
      {
        provider: 'RAZORPAY_KEY_ID',
        label: 'Razorpay Key ID',
        placeholder: 'rzp_test_… or rzp_live_…',
        help: 'Razorpay Dashboard → Account & Settings → API Keys.',
        secret: false,
        testable: true,
      },
      {
        provider: 'RAZORPAY_KEY_SECRET',
        label: 'Razorpay Key Secret',
        placeholder: '••••••••',
        help: 'Shown once when keys are generated. Stored encrypted on the server.',
        secret: true,
        testable: false,
        testWith: 'RAZORPAY_KEY_ID',
      },
      {
        provider: 'RAZORPAY_WEBHOOK_SECRET',
        label: 'Razorpay Webhook Secret',
        placeholder: '••••••••',
        help: 'Dashboard → Webhooks → add https://astrologermatch.com/api/v1/payments/razorpay/webhook with events payment.captured and payment.failed. Credits the wallet even if the browser closes mid-payment.',
        secret: true,
        testable: true,
      },
    ],
  },
  {
    group: 'Email (verification codes & receipts)',
    items: [
      {
        provider: 'SMTP_HOST',
        label: 'SMTP host',
        placeholder: 'smtp.zoho.in',
        help: "Your mail provider's SMTP server, e.g. smtp.zoho.in.",
        secret: false,
        testable: false,
        testWith: 'SMTP_PASSWORD',
      },
      {
        provider: 'SMTP_PORT',
        label: 'SMTP port',
        placeholder: '587',
        help: '587 (STARTTLS) or 465 (SSL).',
        secret: false,
        testable: false,
        testWith: 'SMTP_PASSWORD',
      },
      {
        provider: 'SMTP_USER',
        label: 'SMTP username (also the From address)',
        placeholder: 'noreply@astrologermatch.com',
        secret: false,
        testable: false,
        testWith: 'SMTP_PASSWORD',
      },
      {
        provider: 'SMTP_PASSWORD',
        label: 'SMTP password / app password',
        placeholder: '••••••••',
        secret: true,
        testable: true,
      },
    ],
  },
  {
    group: 'Bot protection — Cloudflare Turnstile',
    items: [
      {
        provider: 'TURNSTILE_SITE_KEY',
        label: 'Turnstile site key',
        placeholder: '0x4AAAAAAA…',
        help: 'Cloudflare dashboard → Turnstile → add site. The site key is public (it is placed in the page).',
        secret: false,
        testable: false,
        testWith: 'TURNSTILE_SECRET_KEY',
      },
      {
        provider: 'TURNSTILE_SECRET_KEY',
        label: 'Turnstile secret key',
        placeholder: '0x4AAAAAAA…',
        help: 'Used by the server to verify the check on registration, login and the review form.',
        secret: true,
        testable: true,
      },
    ],
  },
];

export const ALL_API_KEY_PROVIDERS: ApiKeyProviderName[] = API_KEY_GROUPS.flatMap((g) =>
  g.items.map((i) => i.provider),
);

export interface ApiKeyStatus {
  provider: ApiKeyProviderName;
  configured: boolean;
  preview: string | null; // e.g. "••••1234" for secrets, full value for non-secrets
  lastTestedAt: string | null;
  lastTestOk: boolean | null;
}

export interface ApiKeyTestResult {
  ok: boolean;
  message: string;
  detail?: string;
  testedAt: string;
}

// "••••" plus the last 4 characters (secrets), or the value itself (non-secrets like hosts).
export function maskKey(value: string, secret: boolean): string {
  if (!secret) return value;
  return value.length <= 4 ? '••••' : `••••${value.slice(-4)}`;
}
